import { isAxiosError } from 'axios';
import { ALL_CLIENTS, type RequestMeta } from './client';
import { useAuthStore } from '@core/stores/auth.store';
import { notify } from '@core/notify/toast';
import { toMessage } from '@core/errors/toMessage';

/**
 * Route prefixes on which a 401 must NOT trigger a logout redirect — otherwise a
 * failed login would bounce the user off the login page. Ported from
 * `auth.interceptor.ts` lines 54-58.
 *
 * `/register`, `/reset-password` and `/validate-otp` are included because the
 * Angular code special-cased them, even though no such routes exist in
 * `app.routes.ts`. Keeping them is free and makes adding those routes safe.
 */
const AUTH_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/validate-otp'];

/** Request URL fragments that are themselves auth calls (Angular line 61). */
const AUTH_ENDPOINTS = ['/login', '/register', '/forgot-password'];

function isOnAuthRoute(pathname: string): boolean {
  return AUTH_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/**
 * Installs the request/response interceptors on all three axios instances.
 *
 * This is THE SINGLE ERROR-TOAST LAYER. `ApiBaseService.handleError` is not ported;
 * its per-call fallback strings became `config.meta.fallbackMessage`. In the Angular
 * app both layers fired, so a failed request commonly produced two identical
 * snackbars.
 *
 * Call once, before rendering.
 */
export function installInterceptors(): void {
  for (const client of ALL_CLIENTS) {
    client.interceptors.request.use((config) => {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }

      // Angular parity (auth.interceptor.ts:32-35): JSON content-type only for
      // non-GET requests that are not FormData. For FormData the header must be
      // DELETED, not set — the browser has to supply the multipart boundary.
      if (config.data instanceof FormData) {
        config.headers.delete('Content-Type');
      } else if ((config.method ?? 'get').toUpperCase() !== 'GET') {
        config.headers.set('Content-Type', 'application/json');
      }

      return config;
    });

    client.interceptors.response.use(
      (response) => response,
      (error: Error) => {
        const meta: RequestMeta = (isAxiosError(error) ? error.config?.meta : undefined) ?? {};

        // An aborted request is not a failure. TanStack Query cancels in-flight
        // queries on key change (which is how typeahead cancel-previous works), so
        // toasting these would produce a toast on every keystroke.
        if (isAxiosError(error) && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')) {
          return Promise.reject(error);
        }

        if (isAxiosError(error) && error.response?.status === 401 && !meta.skipAuthRedirect) {
          const onAuthRoute = isOnAuthRoute(window.location.pathname);
          const authCall = isAuthEndpoint(error.config?.url);

          if (!onAuthRoute && !authCall) {
            // Session expired. Suppress the toast — `localLogout` surfaces its own
            // message — and log out. There is no refresh token to try.
            useAuthStore.getState().localLogout();
            notify.error('Session expired. Please login again.');
            return Promise.reject(error);
          }
          // On an auth page a 401 means bad credentials; fall through so the
          // server's message ("Invalid credentials") is shown.
        }

        if (!meta.silent) {
          notify.error(toMessage(error, meta.fallbackMessage));
        }

        if (import.meta.env.DEV) {
          console.error('HTTP Error', {
            url: isAxiosError(error) ? error.config?.url : undefined,
            status: isAxiosError(error) ? error.response?.status : undefined,
            error,
          });
        }

        return Promise.reject(error);
      },
    );
  }
}
